import { cn } from '@/lib/utils/cn';
import { createIconElement } from '@/lib/utils/createIconElement';
import { StanTooltip } from '@/components';
import type { ProductTypeConfig } from '../../types/productType.types';

interface ProductTypeCardProps {
    productType: ProductTypeConfig;
    onClick?: (productType: ProductTypeConfig) => void;
}

export function ProductTypeCard({ productType, onClick }: ProductTypeCardProps) {
    const Icon = createIconElement(productType.icon, { className: 'size-18', removeFill: false });

    const handleClick = () => {
        if (productType.enabled && onClick) {
            onClick(productType);
        }
    };

    const cardContent = (
        <div
            className={cn(
                'border-neutral-subtle shadow-nucleus-elevation-low bg-surface flex min-h-28 items-center gap-2 rounded-lg border px-4 transition-all duration-200',
                productType.enabled &&
                    'hover:shadow-nucleus-elevation-medium hover:border-brand-main cursor-pointer hover:-translate-y-0.5 focus:outline-none',
                !productType.enabled && 'bg-disabled cursor-not-allowed opacity-50'
            )}
            onClick={handleClick}
            role={productType.enabled ? 'button' : undefined}
            tabIndex={productType.enabled ? 0 : -1}
        >
            <div className={cn('flex size-20 items-center justify-center rounded-md')}>{Icon}</div>
            <div className='flex flex-1 flex-col gap-1'>
                <h3 className='text-heading-3 text-dark m-0'>{productType.title}</h3>
                <p className='text-body-sm text-gray m-0 leading-relaxed'>{productType.description}</p>
            </div>
        </div>
    );

    // Wrap with tooltip if product is disabled and has a tooltip message
    if (!productType.enabled && productType.disabledTooltip) {
        return (
            <StanTooltip
                position='bottom'
                content={<span dangerouslySetInnerHTML={{ __html: productType.disabledTooltip }} />}
            >
                {cardContent}
            </StanTooltip>
        );
    }

    return cardContent;
}
